// @ds/display/StatusChipShell — canonical chip renderer for any domain enum.
//
// Captures the three-branch render pattern:
//   1. tone=null OR label=null  →  em-dash span (missing value)
//   2. variant=inline           →  tone-colored span (table-density)
//   3. variant=chip (default)   →  Pill chrome (KV rows, headers)
//
// Use directly with a tone lookup + i18n label whenever you'd otherwise
// reach for a `<Pill tone={TONE_MAP[x]}>{t(...)}</Pill>` pattern. Tone maps
// live in `@ds/tokens/domainTones` — never inline a `Record<Enum, ToneName>`
// in a consumer file. i18n keys live in the corresponding `src/locales/pl/*`.
//
// Example:
//   const tone  = status ? INVOICE_REVIEW_STATUS_COLORS[status] : null;
//   const label = status ? t(`invoices.review_status.${status}`, status) : null;
//   return <StatusChipShell tone={tone} label={label} size="sm" />;
//
// A few domain enums ship a dedicated chip component (`InvoiceDirectionChip`,
// `CompanyChip`, `EdprDocumentStatusChip`, …) when the chip carries logic
// beyond `tone + label` — e.g. an inline badge, derived state, or a
// non-trivial label transform. Per-enum chips are NOT the default — most
// enums render via `StatusChipShell` directly.

import React from "react";
import { Pill, type PillSize, type PillTone } from "@ds/display/Pill";
import { TONE } from "@ds/tokens/palette";

// ─── Inputs ───────────────────────────────────────────────────────────────────

export interface StatusChipShellInput {
  /** Resolved tone. `null` → em-dash. */
  tone: PillTone | null;
  /** Resolved label text. `null` → em-dash. */
  label: string | null;
  /** Rendering variant. Default `"chip"`. */
  variant?: "chip" | "inline";
  /** Pill size — only meaningful when `variant="chip"`. Default `"md"`. */
  size?: PillSize;
  /** `data-testid` on the root element. */
  testId?: string;
}

export type StatusChipShellProps = StatusChipShellInput;

// ─── Component ────────────────────────────────────────────────────────────────

export function StatusChipShell({
  tone,
  label,
  variant = "chip",
  size = "md",
  testId,
}: StatusChipShellProps) {
  if (tone === null || label === null) {
    return <span data-testid={testId}>—</span>;
  }

  if (variant === "inline") {
    return (
      <span data-testid={testId} style={{ color: TONE[tone].text }}>
        {label}
      </span>
    );
  }

  return (
    <Pill tone={tone} size={size} testId={testId}>
      {label}
    </Pill>
  );
}
